import classnames from "classnames";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGCBankingData } from "@krea/customer-features/hooks/useGCBankingData";
import VendorLogo from "@krea/customer-features/images/svg/banking-data/GoCardless-Logo.png";
import Button from "@krea/customer-features/shared-components/button";
import Preloader from "@krea/customer-features/shared-components/preloader";
import ResponsiveDialog from "@krea/customer-features/shared-components/responsive-dialog";
import { Text } from "@krea/customer-features/shared-components/text";
import { IInstitution } from "@krea/customer-features/store/banking-data/types";

import { AccordionItem } from "../accordion/AccordionItem";
import { CheckMark } from "../icon";

const StatusMessage = ({
  className,
  text,
}: {
  className?: string;
  text?: string | null;
}) => {
  return (
    <div className={twMerge("tw-flex tw-items-center", className)}>
      <CheckMark />

      <Text size="sm" className="tw-font-bold">
        {text}
      </Text>
    </div>
  );
};

interface IBankingDataProps {
  resourceId: string;
  resourceType: string;
  className?: string;
  isOnboarded: boolean;
  onSuccess?: () => void;
  isPrivatePerson?: boolean;
  lightTheme?: boolean;
  isGuarantorsForm?: boolean;
}

const BankingData = ({
  resourceId,
  resourceType,
  onSuccess,
  className,
  isOnboarded,
  isPrivatePerson,
  lightTheme,
  isGuarantorsForm = false,
}: IBankingDataProps) => {
  const { t } = useTranslation();

  const onSuccessEvent = () => {
    setIsInitialized(false);
    setHasStarted(false);

    if (onSuccess) {
      onSuccess();
    }
  };
  const [hasStarted, setHasStarted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const { institutionsQuery, initialize, reset, isError, isCancelled } =
    useGCBankingData({ resourceId, resourceType, onSuccess: onSuccessEvent });

  useEffect(() => {
    if (isError || isCancelled) {
      setIsInitialized(false);
      setHasStarted(false);
      reset(!isError);
    }
  }, [isError, isCancelled]);

  const onClickHandler = async () => {
    setHasStarted(true);
  };

  const onInstitutionClick = async (institutionId: string) => {
    setIsInitialized(true);
    initialize(institutionId);
  };

  const recommendedInstitutions = institutionsQuery.data?.filter(
    (it) => it.isRecommended,
  );
  const nonRecommendedInstitutions = institutionsQuery.data?.filter(
    (it) => !it.isRecommended,
  );

  return (
    <div className={classnames(className)}>
      <div
        className={classnames("xl:tw-px-5", {
          "tw-bg-white": lightTheme,
          "tw-bg-gray": !lightTheme && !isGuarantorsForm,
          "xl:tw-py-4 tw-p-3 tw-rounded xl:tw-px-5": !isGuarantorsForm,
          "px-5": isGuarantorsForm,
        })}
      >
        {isPrivatePerson ? (
          <>
            {isGuarantorsForm && !isOnboarded && (
              <div className="tw-flex tw-mt-3 tw-mb-1">
                <Text size="sm">
                  {t("openBankingData.isPrivate.optional")}
                  <a
                    style={{ cursor: "pointer" }}
                    className="tw-text-primary tw-font-bold"
                    onClick={onClickHandler}
                  >
                    {t("openBankingData.isPrivate.title")}
                  </a>
                </Text>
              </div>
            )}
            {!isGuarantorsForm && (
              <Text size="sm" className="tw-mb-3 tw-font-bold">
                {t("openBankingData.isPrivate.title")}
              </Text>
            )}

            {isOnboarded && (
              <StatusMessage
                className="tw-pt-1"
                text={t("openBankingData.success")}
              />
            )}

            {!isOnboarded && !isGuarantorsForm && (
              <Button variant="outlined" size="sm" onClick={onClickHandler}>
                {t("openBankingData.isPrivate.cta")}
              </Button>
            )}
          </>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center">
            <div className="tw-flex tw-flex-col tw-items-center">
              <div
                className={classnames(
                  "tw-flex tw-flex-row tw-w-full tw-items-center tw-justify-center tw-mb-3",
                )}
              >
                {/*<span className="tw-mr-1">Via</span>*/}
                <span>
                  <img
                    src={VendorLogo}
                    alt="GoCardless logo"
                    className="tw-h-auto tw-w-[120px]"
                  />
                </span>
              </div>

              <Text size="sm" className="tw-font-bold">
                {t("openBankingData.isCompany.title")}
              </Text>
            </div>

            {isOnboarded && (
              <StatusMessage
                className="tw-mt-3"
                text={t("openBankingData.success")}
              />
            )}

            <div className="tw-flex tw-items-center tw-justify-center tw-pt-4">
              <Button size="sm" variant="outlined" onClick={onClickHandler}>
                {isOnboarded
                  ? t("openBankingData.isCompany.cta.isOnboarded")
                  : t("openBankingData.isCompany.cta.notOnboarded")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <ResponsiveDialog
        onClose={() => {
          reset();
          setHasStarted(false);
          setIsInitialized(false);
        }}
        title={t("openBankingData.chooseBank")}
        open={hasStarted}
        dialogContentStyling="tw-max-h-[90vh] tw-min-h-[200px]"
        drawerContentStyling=""
        setOpen={() => {}}
      >
        <>
          {institutionsQuery.isFetching || isInitialized ? (
            <Preloader size="md" className="" />
          ) : (
            <div>
              <div className="tw-px-6">
                {recommendedInstitutions?.map((institution) => (
                  <InstitutionItem
                    institution={institution}
                    onInstitutionClick={onInstitutionClick}
                  />
                ))}
              </div>
              {nonRecommendedInstitutions &&
                nonRecommendedInstitutions.length > 0 && (
                  <AccordionItem
                    id="show-more-institutions"
                    title={t("openBankingData.showMore")}
                    titleStyling="tw-text-base tw-font-bold tw-mb-0 tw-text-primary"
                    className="tw-border-0 tw-px-0"
                  >
                    <>
                      {nonRecommendedInstitutions?.map((institution) => (
                        <InstitutionItem
                          institution={institution}
                          onInstitutionClick={onInstitutionClick}
                        />
                      ))}
                    </>
                  </AccordionItem>
                )}
            </div>
          )}
        </>
      </ResponsiveDialog>

      {isError && (
        <Text
          size="sm"
          className="tw-font-bold tw-text-red-600 tw-mt-2 tw-ml-5"
        >
          {t("openBankingData.error")}
        </Text>
      )}
    </div>
  );
};

const InstitutionItem = ({
  institution,
  onInstitutionClick,
}: {
  institution: IInstitution;
  onInstitutionClick: (institutionId: string) => void;
}) => {
  return (
    <div
      key={institution.id}
      onClick={() => {
        onInstitutionClick(institution.id);
      }}
      className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-h-[80px] tw-cursor-pointer tw-p-4 tw-border tw-border-gray-200 tw-rounded tw-mb-2 tw-bg-gray-100 hover:tw-bg-gray-200"
    >
      <Text size="lg" className="tw-font-bold">
        {institution.name}
      </Text>
      <img
        className="tw-w-[60px]"
        src={institution.logo}
        alt={institution.name}
      />
    </div>
  );
};

export default BankingData;
